import {
  FlexCell,
  FlexRow,
  LabeledInput,
  PickerInput,
  RadioGroup,
  Tooltip,
  IconButton,
} from '@epam/promo';
import { ILens, LazyDataSourceApiRequest, useLazyDataSource } from '@epam/uui';
import { MAX_INDUSTRIES } from 'constants/uiConstants';
import { Industry } from 'modules/project-list/components/project-card/modal';
import { ProjectDetails } from 'modules/project-management/models/interfaces';
import { useCallback } from 'react';
import { ReactComponent as infoIcon } from '@epam/assets/icons/common/notification-info-outline-18.svg';
import { Counter } from '../counter';
import getIndustryResponse from './utils';
import './style.scss';
import { useIndustries } from 'hooks/useIndustries';
import { anyIndustryValue } from 'utils/helpers';
import { useState, useEffect } from 'react';

export function Industries({
  lens,
  industryTypeLens,
  isRequired = false,
  onlyIndustriesRequired = false,
}: {
  lens: ILens<ProjectDetails['industries']>;
  industryTypeLens?: ILens<ProjectDetails['industryType']>;
  isRequired?: boolean;
  onlyIndustriesRequired?: boolean;
}) {
  const loadIndustries = useCallback(
    (request: LazyDataSourceApiRequest<Industry, string, unknown>) => {
      return getIndustryResponse(request);
    },
    []
  );

  const industryData = useLazyDataSource(
    {
      api: loadIndustries,
      selectAll: false,
    },
    []
  );

  const { industryType, setIndustryType } = useIndustries(
    lens.toProps().value,
    industryTypeLens?.toProps().value
  );
  const [toggle, setToggle] = useState<boolean>(false);

  useEffect(() => {
    if (industryType === 'any') {
      const fetchAny = async () => {
        const anyIndustryVal = await anyIndustryValue();
        lens.set(anyIndustryVal);
        industryTypeLens?.set('any');
      };
      fetchAny();
    }

    if (industryType === 'specific') {
      if (toggle) lens.set([]);
      industryTypeLens?.set('specific');
    }
  }, [toggle, industryType]);

  function renderSpecificIndustry() {
    // Filter out "Any Industry" (externalId === '-1') from the value
    const specificIndustries = (lens.toProps().value || []).filter(
      (industry) => industry.externalId !== '-1'
    );
    return (
      <FlexCell
        rawProps={{ 'data-auto-scroll': '' }}
        style={{ marginTop: '15px' }}
      >
        <LabeledInput
          label="Specific Industries"
          {...lens.toProps()}
          cx={'specific-industries-label'}
          isRequired
        >
          <PickerInput
            dataSource={industryData}
            selectionMode={'multi'}
            valueType={'entity'}
            value={specificIndustries}
            dropdownPlacement="auto"
            onValueChange={(newValue) => {
              // Ensure we only save specific industries (not "Any Industry")
              const filteredValue = (newValue || []).filter(
                (industry) => industry.externalId !== '-1'
              );
              lens.toProps().onValueChange(filteredValue);
            }}
            placeholder="Please select up to 3 industries"
            entityName="Specific Industry"
            getRowOptions={(item: Industry) => ({
              checkbox: {
                isVisible: true,
                isDisabled:
                  specificIndustries.length >= MAX_INDUSTRIES &&
                  !specificIndustries.find((industry) => {
                    if (industry && industry.id === item.id) return industry;
                  }),
              },
            })}
          />
        </LabeledInput>
        <Counter
          currentValue={specificIndustries.length}
          maxValue={MAX_INDUSTRIES}
        />
      </FlexCell>
    );
  }

  return (
    <FlexRow vPadding={'24'}>
      <FlexCell grow={1}>
        <LabeledInput
          label={
            <FlexRow alignItems="center" spacing="12">
              <span style={onlyIndustriesRequired ? { margin: 0 } : undefined}>
                Industries
              </span>
              {(isRequired || onlyIndustriesRequired) && (
                <span style={{ color: '#E54322', marginLeft: 0 }}>*</span>
              )}
              <Tooltip
                content={
                  <div className="industries-tooltip-content">
                    <p>Set the industry scope for your project:</p>
                    <ul>
                      <li>
                        <strong>Any Industry:</strong> Universal solutions
                        applicable to many industries.
                      </li>
                      <li>
                        <strong>Specific Industry:</strong> Choose up to 3
                        industries relevant to your solution.
                      </li>
                    </ul>
                  </div>
                }
                placement="right"
              >
                <IconButton
                  icon={infoIcon}
                  cx="industries-tooltip-icon"
                  rawProps={{ style: { marginTop: '0px', marginLeft: '0px' } }}
                />
              </Tooltip>
            </FlexRow>
          }
          cx={'industries'}
        >
          <div className="industries-radio-group">
            <FlexRow spacing="6" alignItems="top">
              <RadioGroup
                cx="industry-radio-group"
                items={[
                  { id: 'any', name: 'Any Industry' },
                  { id: 'specific', name: 'Specific Industry' },
                ]}
                value={industryType}
                onValueChange={(newValue) => {
                  setIndustryType(newValue);
                  setToggle(true);
                }}
              />
            </FlexRow>
            {industryType === 'specific' && (
              <div className="industries-dropdown-row">
                {renderSpecificIndustry()}
              </div>
            )}
          </div>
        </LabeledInput>
      </FlexCell>
    </FlexRow>
  );
}
